import { LocalizedProperty } from "../common";
import { OverviewDto } from "../common/overview.interface";
import { MediaDto } from "../media";
import {
  LiveLectureAccessModeEnum,
  LiveLectureBookingPaymentModeEnum,
  LiveLectureBookingStatusEnum,
  LiveLectureStatusEnum,
  LiveLectureTypeEnum,
} from "../../enums";

export interface LiveLectureTeacherDto {
  id: string;
  name: LocalizedProperty;
  profilePicture?: string;
}

export interface LiveLectureAcademicPathDto {
  educationSystem?: OverviewDto;
  curriculum?: OverviewDto;
  stage?: OverviewDto;
  course?: OverviewDto;
  grade?: OverviewDto;
}

export interface LiveLectureDto {
  id: string;
  title: LocalizedProperty;
  description?: LocalizedProperty;
  type?: LiveLectureTypeEnum;
  status: LiveLectureStatusEnum;
  accessMode: LiveLectureAccessModeEnum;
  subjectId?: string;
  subject?: OverviewDto;
  academicPath?: LiveLectureAcademicPathDto;
  teacherUserId: string;
  teacher?: LiveLectureTeacherDto;
  categoryId?: string;
  category?: OverviewDto;
  courseDurationId?: string;
  startAt: Date;
  endAt: Date;
  durationMinutes: number;
  maxParticipants?: number;
  bookedCount: number;
  availableSeats?: number;
  price: number;
  currency: string;
  meetingRoomId?: string;
  media?: MediaDto[];
  isActive: boolean;
  createdAt: Date;
}

export interface CreateLiveLectureRequest {
  title: LocalizedProperty;
  description?: LocalizedProperty;
  type?: LiveLectureTypeEnum;
  accessMode?: LiveLectureAccessModeEnum;
  status?: LiveLectureStatusEnum;
  subjectId?: string;
  teacherUserId?: string;
  categoryId?: string;
  courseDurationId?: string;
  gradeId?: string;
  courseId?: string;
  startAt: Date;
  endAt: Date;
  durationMinutes: number;
  maxParticipants?: number;
  price?: number;
  currency?: string;
  mediaIds?: string[];
}

export interface UpdateLiveLectureRequest {
  title?: LocalizedProperty;
  description?: LocalizedProperty;
  type?: LiveLectureTypeEnum;
  accessMode?: LiveLectureAccessModeEnum;
  status?: LiveLectureStatusEnum;
  subjectId?: string;
  teacherUserId?: string;
  categoryId?: string;
  courseDurationId?: string;
  gradeId?: string;
  courseId?: string;
  startAt?: Date;
  endAt?: Date;
  durationMinutes?: number;
  maxParticipants?: number;
  price?: number;
  currency?: string;
  mediaIds?: string[];
}

export interface StandaloneLiveLectureStatsDto {
  total: number;
  incoming: number;
  thisMonthTotal: number;
  lastMonthTotal: number;
  published: number;
  unpublished: number;
}

export type LiveLectureStatsDto = StandaloneLiveLectureStatsDto;

export interface LiveLectureMeetingLinkDto {
  bookingId?: string;
  liveLectureId: string;
  callingUrl: string;
  namespace: string;
  roomId: string;
  mediaRole: "host" | "viewer" | "moderator";
  token: string;
  expiresIn: string;
  startAt: Date;
  endAt: Date;
  canJoin: boolean;
  startMuted?: boolean;
}

export interface GetLiveLecturesInRangeRequest {
  startAt: string;
  endAt: string;
  educationSystemId?: string;
  curriculumId?: string;
  courseId?: string;
  stageId?: string;
  gradeId?: string;
  subjectId?: string;
  accessMode?: LiveLectureAccessModeEnum;
}

export interface GetLiveLectureStatsQuery {
  educationSystemId?: string;
  curriculumId?: string;
  courseId?: string;
  stageId?: string;
  gradeId?: string;
  subjectId?: string;
}

export const LIVE_LECTURE_SLOT_DURATIONS = [30, 45, 60, 90] as const;
export type LiveLectureSlotDuration =
  (typeof LIVE_LECTURE_SLOT_DURATIONS)[number];

export interface AvailableScheduleSlotsRequest {
  subjectId: string;
  teacherUserId: string;
  type: LiveLectureTypeEnum;
  startAt: string;
  endAt?: string;
  timezone: string;
  currentTime: string;
  slotDurationMinutes?: LiveLectureSlotDuration;
  courseDurationId?: string;
  gradeId?: string;
  courseId?: string;
  excludeLiveLectureId?: string;
}

export interface LiveLectureBookingParticipantDto {
  id: string;
  participantUserId?: string;
  participantName?: string;
  participantEmail?: string;
}

export interface LiveLectureBookingUserDto {
  id: string;
  name: LocalizedProperty;
  phone?: string;
}

export interface LiveLectureBookingAdminDto {
  id: string;
  studentUserId: string;
  student?: LiveLectureBookingUserDto;
  bookedByUserId: string;
  bookedBy?: LiveLectureBookingUserDto;
  status: LiveLectureBookingStatusEnum;
  bookingPaymentMode?: LiveLectureBookingPaymentModeEnum;
  subtotal: number;
  tax: number;
  total: number;
  studyNotes?: string;
  participants?: LiveLectureBookingParticipantDto[];
  createdAt: Date;
}

export interface LiveLectureBookingsDetailDto {
  liveLectureId: string;
  title: LocalizedProperty;
  startAt: Date;
  endAt: Date;
  maxParticipants?: number;
  bookedCount: number;
  availableSeats?: number;
  bookings: LiveLectureBookingAdminDto[];
}

export interface LiveLectureSessionParticipantDto {
  id: string;
  name: string;
  isGuest?: boolean;
  firstJoinedAt?: Date;
  lastJoinedAt?: Date;
  joinCount: number;
}

export interface LiveLectureSessionActivityDto {
  liveLectureId: string;
  status: LiveLectureStatusEnum;
  scheduledStartAt: Date;
  scheduledEndAt: Date;
  startedAt?: Date;
  endedAt?: Date;
  isOngoing: boolean;
  participants: LiveLectureSessionParticipantDto[];
}

export interface LiveLectureInvitationDto {
  id: string;
  liveLectureId: string;
  invitedByUserId: string;
  token: string;
  inviteUrl: string;
  guestName?: string;
  firstJoinedAt?: Date;
  lastJoinedAt?: Date;
  createdAt: Date;
}